////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//      Help functions to send http requests...                               //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useEffect, useState } from "react";

export function useAwaitResponse<T, I>(
  initialValue: I,
  callable: () => Promise<T>
): [T | I, boolean, React.Dispatch<React.SetStateAction<T | I>>];
export function useAwaitResponse<T, I, M>(
  initialValue: I,
  callable: () => Promise<T>,
  responseMap?: (response: T) => M
): [I | M, boolean, React.Dispatch<React.SetStateAction<I | M>>];
export function useAwaitResponse<T, I, M>(
  initialValue: I,
  callable: () => Promise<T>,
  responseMap?: (response: T) => M
):
  | [T | I, boolean, React.Dispatch<React.SetStateAction<T | I>>]
  | [M | I, boolean, React.Dispatch<React.SetStateAction<M | I>>] {
  const [response, setResponse] = useState<M | I | T>(initialValue);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const resolveData = async () => {
      try {
        const response = await callable();
        if (isMounted) {
          setResponse(responseMap ? responseMap(response) : response);
        }
      } catch (e) {
        console.error("AwaitAsyncResponse threw an exception: ", e);
      } finally {
        setIsLoading(false);
      }
    };

    resolveData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (responseMap) {
    return [
      response as T | I,
      isLoading as boolean,
      setResponse as React.Dispatch<React.SetStateAction<T | I>>,
    ];
  } else {
    return [
      response as M | I,
      isLoading as boolean,
      setResponse as React.Dispatch<React.SetStateAction<M | I>>,
    ];
  }
}

export function successfulResponse(responseCode: number) {
  return responseCode >= 200 && responseCode < 300;
}

export async function requestGET<ResponseType>(url: string, headers?: object) {
  const request: GenericRequest = {
    headers: headers ?? {},
    requestType: "GET",
    url: url,
  };
  return sendRequest<ResponseType>(request);
}

export async function requestPOST<ResponseType>(
  url: string,
  body: Object,
  headers?: object
) {
  const request: GenericRequest = {
    headers: headers ?? {},
    requestType: "POST",
    url: url,
    body: body,
  };
  return sendRequest<ResponseType>(request);
}

export async function requestPUT<ResponseType>(
  url: string,
  body: Object | undefined
) {
  const request: GenericRequest = {
    headers: {},
    requestType: "PUT",
    url: url,
    body: body,
  };
  return sendRequest<ResponseType>(request);
}

export async function requestDELETE<ResponseType>(url: string) {
  const request: GenericRequest = {
    headers: {},
    requestType: "DELETE",
    url: url,
    body: undefined,
  };
  return sendRequest<ResponseType>(request);
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

interface GenericRequest {
  requestType: "POST" | "GET" | "PUT" | "DELETE";
  url: string;
  headers: object;
  body?: object;
}

export interface Response<T> {
  body?: T;
  status: number;
}

async function sendRequest<ResponseType>(
  request: GenericRequest
): Promise<{ status: number; body?: ResponseType }> {
  const axiosConfig: AxiosRequestConfig = {
    method: request.requestType,
    url: request.url,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...request.headers,
    },
    withCredentials: true,
    data: request.body,
    validateStatus: () => true, // Always return response, even non-2xx
  };

  try {
    const response: AxiosResponse<ResponseType> = await axios(axiosConfig);

    return {
      status: response.status,
      body: response.data,
    };
  } catch (error: any) {
    console.error("RequestError:", error, "for request", request);

    return {
      status: error.response?.status ?? -1,
      body: undefined,
    };
  }
}
